// Presentational pieces shared by the library's panes: the compact/detailed layout switch, role
// tiles, and the read-only roster.

import { createContext, useContext, useState } from "react";
import { LayoutGrid, List } from "lucide-react";
import { TEAM_ORDER } from "@cc/shared";
import type { Script, ScriptCharacter, Team } from "@cc/shared";
import { cn, teamColor, teamLabel } from "../../../lib/ui.js";
import { Button, Tooltip } from "../../ui/primitives.js";

/** Compact tiles, or one full-width card per role with its ability text — like grid vs detail. */
export type Layout = "grid" | "detail";
export const LAYOUT_KEY = "cc:script-layout";
export const LayoutContext = createContext<{ layout: Layout; setLayout: (l: Layout) => void }>({
  layout: "grid",
  setLayout: () => {},
});

export function readLayout(): Layout {
  try {
    return localStorage.getItem(LAYOUT_KEY) === "detail" ? "detail" : "grid";
  } catch {
    return "grid";
  }
}

export function LayoutToggle({ className }: { className?: string }) {
  const { layout, setLayout } = useContext(LayoutContext);
  return (
    <div className={cn("flex gap-1", className)} role="group" aria-label="Role layout">
      {(
        [
          ["grid", "Compact tiles", LayoutGrid],
          ["detail", "Detailed cards", List],
        ] as const
      ).map(([value, tip, Icon]) => (
        <Tooltip key={value} content={tip}>
          <Button
            size="sm"
            variant={layout === value ? "primary" : "secondary"}
            aria-label={tip}
            aria-pressed={layout === value}
            onClick={() => setLayout(value)}
          >
            <Icon className="size-4" />
          </Button>
        </Tooltip>
      ))}
    </div>
  );
}

export function TileGroup({ children }: { children: React.ReactNode }) {
  const { layout } = useContext(LayoutContext);
  return <div className={cn("flex gap-1", layout === "detail" ? "flex-col" : "flex-wrap")}>{children}</div>;
}

/** The character's icon, or their initial on a team-coloured disc when there is no picture to show. */
function RoleIcon({
  character,
  className,
}: {
  character: Pick<ScriptCharacter, "name" | "icon" | "team">;
  className: string;
}) {
  const [failed, setFailed] = useState(false);
  if (character.icon && !failed) {
    return (
      <img
        src={character.icon}
        alt=""
        onError={() => setFailed(true)}
        className={cn("shrink-0 object-contain", className)}
      />
    );
  }
  return (
    <span
      aria-hidden
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full border text-xs font-semibold uppercase text-parchment",
        className,
      )}
      style={{ borderColor: teamColor(character.team), background: `${teamColor(character.team)}55` }}
    >
      {character.name.charAt(0)}
    </span>
  );
}

/** The same tile as the seat dialog's character list; `onClick` makes it a toggle. */
export function RoleTile({
  character,
  selected = false,
  onClick,
}: {
  character: Pick<ScriptCharacter, "id" | "name" | "icon" | "ability" | "team">;
  selected?: boolean;
  onClick?: () => void;
}) {
  const { layout } = useContext(LayoutContext);
  const detail = layout === "detail";
  const body = detail ? (
    <>
      <RoleIcon character={character} className="size-10" />
      <span className="min-w-0 text-left">
        <span className="block text-sm font-medium text-parchment">{character.name}</span>
        {character.ability && <span className="block text-xs text-parchment-dim">{character.ability}</span>}
      </span>
    </>
  ) : (
    <>
      <RoleIcon character={character} className="size-6" />
      {character.name}
    </>
  );
  const base = detail
    ? "flex w-full items-center gap-3 rounded border px-2 py-1.5 transition"
    : "flex items-center gap-1 rounded border px-1.5 py-1 text-xs transition";
  const tile = onClick ? (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        base,
        selected
          ? "border-brass bg-brass/20 text-parchment"
          : "border-ink-line text-parchment-dim hover:text-parchment",
      )}
    >
      {body}
    </button>
  ) : (
    <span className={cn(base, "border-ink-line text-parchment")}>{body}</span>
  );
  // The card already shows the ability, so only the compact tile needs a tooltip for it.
  return detail ? tile : <Tooltip content={character.ability ?? character.name}>{tile}</Tooltip>;
}

export function TeamHeading({ team, children }: { team: Team | null; children?: React.ReactNode }) {
  return (
    <h4
      className="mb-1 flex items-center gap-2 text-xs font-semibold uppercase tracking-wide"
      style={{ color: teamColor(team) }}
    >
      {team ? teamLabel(team) : "Unrecognised"}
      {children}
    </h4>
  );
}

/** Read-only roster, grouped by team the way the seat dialog groups it. */
export function Roster({ script }: { script: Script }) {
  const groups: (Team | null)[] = [...TEAM_ORDER, null];
  return (
    <div className="cc-scroll cc-scroll-strong min-h-0 flex-1 overflow-y-auto pr-1">
      {groups
        .filter((t) => script.characters.some((c) => c.team === t))
        .map((team) => (
          <div key={team ?? "unknown"} className="mb-2">
            <TeamHeading team={team} />
            <TileGroup>
              {script.characters
                .filter((c) => c.team === team)
                .map((c) => (
                  <RoleTile key={c.id} character={c} />
                ))}
            </TileGroup>
          </div>
        ))}
    </div>
  );
}

/** "Townsfolk 5 · Outsider 2 …", in team colours. */
export function TeamSummary({ teams }: { teams: (Team | null)[] }) {
  return (
    <span className="flex flex-wrap items-center gap-x-2 text-xs text-parchment-dim">
      <span>{teams.length} roles</span>
      {TEAM_ORDER.map((t) => {
        const n = teams.filter((x) => x === t).length;
        return n > 0 ? (
          <span key={t} style={{ color: teamColor(t) }}>
            {teamLabel(t)} {n}
          </span>
        ) : null;
      })}
    </span>
  );
}
